/**
 * Встроенные команды поля ввода — как в Discord: «/shrug», «/me» и другие.
 * Разбираются здесь же, на клиенте: на сервер уходит готовый текст, а «/nick»
 * становится сменой ника.
 */
export interface Command {
  name: string;
  /** Что пишут после имени; в квадратных скобках — необязательное. */
  args: string;
  hint: string;
  /** Только на сервере: в переписке ника нет. */
  serverOnly?: boolean;
}

export const COMMANDS: Command[] = [
  { name: "shrug", args: "[сообщение]", hint: "Добавляет ¯\\_(ツ)_/¯" },
  { name: "tableflip", args: "[сообщение]", hint: "Добавляет (╯°□°)╯︵ ┻━┻" },
  { name: "unflip", args: "[сообщение]", hint: "Добавляет ┬─┬ノ( º _ ºノ)" },
  { name: "me", args: "сообщение", hint: "Курсивом — о себе со стороны" },
  { name: "spoiler", args: "сообщение", hint: "Под спойлером" },
  { name: "nick", args: "[ник]", hint: "Свой ник на сервере; пусто — сбросить", serverOnly: true },
  { name: "msg", args: "@человек сообщение", hint: "Написать в личку, не уходя отсюда" },
  { name: "kick", args: "@участник", hint: "Выгнать с сервера — спросит подтверждение", serverOnly: true },
  { name: "ban", args: "@участник", hint: "Забанить — спросит срок и причину", serverOnly: true },
  { name: "timeout", args: "@участник [срок]", hint: "Тайм-аут: 60s, 5m, 1h, 1d, 1w; без срока — час", serverOnly: true },
];

/**
 * Пожимание плечами экранировано: без этого `_(ツ)_` разметка сочла бы
 * курсивом, а обратную косую черту съела бы как знак экранирования.
 */
const SHRUG = "¯\\\\\\_(ツ)\\_/¯";

export type CommandRun =
  | { kind: "send"; content: string }
  | { kind: "nick"; nick: string | null }
  /** Кому — хэндлом, как его вставляет подсказка «@». */
  | { kind: "msg"; handle: string; content: string }
  | { kind: "kick" | "ban"; handle: string }
  | { kind: "timeout"; handle: string; minutes: number }
  /** Знакомая команда с негодными доводами: подсказать, а не отправлять набранное в канал. */
  | { kind: "usage"; text: string };

/** Тайм-аут дольше 28 дней Discord не даёт — и мы тоже. */
const TIMEOUT_MAX_MINUTES = 28 * 24 * 60;

/** Единицы срока: коротко и словами, по-русски и по-английски. */
const UNITS: [RegExp, number][] = [
  // Секунды — делителем ниже, а не дробью: 180 × (1/60) в числах с плавающей точкой даёт 3,0000000000000004.
  [/^(s|sec|с|сек|секунд[аы]?)$/, 0],
  [/^(m|min|м|мин|минут[аы]?)?$/, 1],
  [/^(h|hour|ч|час|часа|часов)$/, 60],
  [/^(d|day|д|дн|дня|дней|день)$/, 1440],
  [/^(w|week|н|нед|неделя|недели|недель)$/, 10080],
];

/** «90s», «5m», «2ч», «1 день», «2 часа», «1w» — в минуты; без единицы — минуты. `null` — не срок. */
export function timeoutMinutes(raw: string): number | null {
  const match = /^(\d+)\s*([a-zа-яё]*)$/i.exec(raw.trim());
  if (!match) return null;
  const unit = UNITS.find(([pattern]) => pattern.test(match[2].toLowerCase()));
  if (!unit) return null;
  const amount = Number(match[1]);
  const minutes = unit[1] === 0 ? Math.ceil(amount / 60) : amount * unit[1];
  return minutes >= 1 && minutes <= TIMEOUT_MAX_MINUTES ? minutes : null;
}

/**
 * Что сделать с набранным. `null` — это не команда (или незнакомая, или
 * без нужного текста): уходит как есть, как в Discord.
 */
export function runCommand(text: string, inServer: boolean): CommandRun | null {
  const match = /^\/([a-z]+)(?:\s+([\s\S]*))?$/i.exec(text.trim());
  if (!match) return null;
  const rest = (match[2] ?? "").trim();
  const tail = (sign: string) => ({ kind: "send" as const, content: rest ? `${rest} ${sign}` : sign });

  switch (match[1].toLowerCase()) {
    case "shrug":
      return tail(SHRUG);
    case "tableflip":
      return tail("(╯°□°)╯︵ ┻━┻");
    case "unflip":
      return tail("┬─┬ノ( º _ ºノ)");
    case "me":
      return rest ? { kind: "send", content: `_${rest}_` } : null;
    case "spoiler":
      return rest ? { kind: "send", content: `||${rest}||` } : null;
    case "nick":
      return inServer ? { kind: "nick", nick: rest || null } : null;
    case "msg": {
      const [handle, content] = target(rest);
      return handle && content ? { kind: "msg", handle, content } : usage("/msg @человек сообщение");
    }
    case "kick":
    case "ban": {
      const name = match[1].toLowerCase() as "kick" | "ban";
      if (!inServer) return { kind: "usage", text: "Это команда сервера" };
      const [handle] = target(rest);
      return handle ? { kind: name, handle } : usage(`/${name} @участник`);
    }
    case "timeout": {
      if (!inServer) return { kind: "usage", text: "Это команда сервера" };
      const [handle, after] = target(rest);
      if (!handle) return usage("/timeout @участник [срок]");
      // Срок — в начале, единица может стоять через пробел: «2 ч», «1 день».
      // Слово после числа, не похожее на единицу, — уже причина: «5 флуд». Без срока — час.
      const withUnit = /^(\d+\s*[a-zа-яё]+)(?=\s|$)/i.exec(after);
      const bare = /^(\d+)(?=\s|$)/.exec(after);
      const minutes = !after
        ? 60
        : (withUnit ? timeoutMinutes(withUnit[1]) : null) ?? (bare ? timeoutMinutes(bare[1]) : null);
      return minutes
        ? { kind: "timeout", handle, minutes }
        : { kind: "usage", text: "Срок тайм-аута: 60s, 5m, 2ч, 1д, 1w — не дольше 28 дней" };
    }
    default:
      return null;
  }
}

const usage = (form: string): CommandRun => ({ kind: "usage", text: `Как: ${form}` });

/** «@хэндл остальное» — кому и что дальше. Без «@» в начале — никому. */
function target(rest: string): [string | null, string] {
  const match = /^@(\S+)\s*([\s\S]*)$/.exec(rest);
  return match ? [match[1], match[2].trim()] : [null, rest];
}

/**
 * «s/было/стало» — как в Discord: правит своё последнее сообщение, заменяя
 * первое вхождение. `null` — это не правка, а обычный текст.
 */
export function quickEdit(text: string): { from: string; to: string } | null {
  const match = /^s\/([^/\n]+)\/([^/\n]*)\/?$/.exec(text.trim());
  return match ? { from: match[1], to: match[2] } : null;
}
